/**
 * ЧТО КАБИНЕТ ХРАНИТ ДЛЯ ЧТЕНИЯ БЕЗ СВЯЗИ (П1-12, решение владельца 47).
 *
 * ПОЧЕМУ ЭТО ОТДЕЛЬНЫЙ ЧИСТЫЙ ФАЙЛ. Состав кеша — не техническая подробность, а РЕШЕНИЕ
 * О ПЕРСОНАЛЬНЫХ ДАННЫХ: в ответе журнала лежат имена клиентов и их телефоны (в пределах
 * прав), и всё, что попало в Cache Storage, остаётся на устройстве до очистки. Телефон
 * мастера — самое частое «чужое устройство» салона: его теряют, меняют, отдают детям.
 * Поэтому перечень адресов задан ПОИМЁННО, проверяется тестом и читается одним взглядом,
 * а не выводится из строчки «кешируй всё, что GET».
 *
 * ПРАВИЛО ОТБОРА ВЗЯТО ИЗ ТЗ 05 §5.14 дословно: «Доступны: свой день и соседние дни,
 * карточки их записей, контакты в пределах прав и настроек. Недоступны: поиск по клиентам,
 * справочники, сводка и прогноз — им нужен запрос». Сводка в кеш НЕ попадает, хотя сводный
 * план П1-12 её упоминает: §5.14 назван в том же плане источником истины, а «сейчас
 * не посчитать» и «показываю старое» — разные сообщения, и путать их нельзя.
 *
 * ЗАПИСЬ НЕ КЕШИРУЕТСЯ НИКОГДА. Ни один POST/PATCH/DELETE не откладывается и не повторяется
 * из кеша: запись, созданная без связи, неразрешимо конфликтует с чужой занятостью
 * (GAPS D4), и обещание «сохраню потом» база отвергнет при синхронизации.
 */

/** Оболочка: разметка и собранные файлы приложения. Версия в имени — ключ вытеснения. */
export const SHELL_CACHE = "plnr-cabinet-shell-v1"

/** Ответы API, разрешённые к чтению без связи. Содержит персональные данные. */
export const DATA_CACHE = "plnr-cabinet-data-v1"

/**
 * Момент, когда ответ попал в кеш, — заголовок, который добавляет service worker.
 *
 * Возраст данных показывается ВСЕГДА (§5.14: «"Показываю расписание" без времени —
 * запрещённая формулировка»), а `generatedAt` из тела ответа для этого не годится: он
 * говорит, когда данные собрал сервер, и у ответа, лежащего в кеше с утра, он утренний
 * — но кабинет обязан отличать «показываю старое из кеша» от «сервер ответил только что».
 * Отличить это по телу нельзя, поэтому отметку ставит тот, кто отдал ответ из кеша.
 */
export const CACHED_AT_HEADER = "x-plnr-cached-at"

/** Все кеши кабинета: их же и стирает выход из учётной записи. */
export const ALL_CACHES: readonly string[] = [SHELL_CACHE, DATA_CACHE]

/**
 * Адреса API, ответы которых можно прочитать без связи.
 *
 * `/v1/cabinet/journal` — день и неделя разом: на нём стоят и журнал стойки (5-А1),
 * и «Мой день» мастера, и «Моя неделя» (5-М2), и карточка визита (5-А3) — панель берёт
 * состав визита из ответа дня, отдельного запроса ей не нужно.
 *
 * `/v1/cabinet/auth/me` — не данные, а КАРКАС: роль, права, филиалы и часовой пояс.
 * Без него кабинет без связи не может даже решить, какие разделы человеку показывать,
 * и вместо расписания из кеша встречает экраном входа — то есть офлайн-чтения нет.
 */
const CACHEABLE_PATHS: readonly string[] = ["/v1/cabinet/journal", "/v1/cabinet/auth/me"]

/**
 * Адреса, которые в кеш не попадают НИКОГДА, даже если совпали префиксом.
 *
 * Здесь не «остальное», а именно те пути, которые начинаются как разрешённые: `/journal/…`
 * даёт причины отмены и экспорт — первое без связи бесполезно (менять нельзя), второе
 * выгружает базу салона на устройство, где ему делать нечего.
 */
const NEVER_CACHE: readonly string[] = [
  "/v1/cabinet/journal/export",
  "/v1/cabinet/journal/cancel-reasons",
]

export function cacheableApiPath(pathname: string): boolean {
  if (NEVER_CACHE.some((deny) => pathname === deny || pathname.startsWith(`${deny}/`))) return false
  return CACHEABLE_PATHS.some((allow) => pathname === allow || pathname.startsWith(`${allow}?`))
}

/**
 * Файл оболочки: разметка, собранные js/css, манифест и значки.
 *
 * Список по РАСШИРЕНИЮ и папке сборки, а не поимённо: имена собранных файлов содержат хеш
 * и меняются с каждым выпуском. Разметка (`/`) кешируется отдельной веткой навигации.
 */
export function shellAsset(pathname: string): boolean {
  if (pathname.startsWith("/assets/")) return true
  return (
    pathname === "/manifest.webmanifest" ||
    pathname === "/favicon.svg" ||
    pathname.startsWith("/icons/")
  )
}

/**
 * Запрос, который вообще может участвовать в кеше.
 *
 * ТОЛЬКО GET И ТОЛЬКО СВОЙ ИСТОЧНИК. Кабинет ходит в API того же источника (cookie
 * с refresh помечена SameSite=Strict), поэтому чужой источник здесь — либо ошибка,
 * либо чужой скрипт, и его ответы в кеше салона не нужны.
 */
export function mayUseCache(input: {
  readonly method: string
  readonly sameOrigin: boolean
  readonly pathname: string
}): "shell" | "data" | null {
  if (input.method !== "GET" || !input.sameOrigin) return null
  if (shellAsset(input.pathname)) return "shell"
  if (cacheableApiPath(input.pathname)) return "data"
  return null
}
